import React, { useId, type ComponentProps } from 'react';

import { Icon, Tooltip } from 'design-system-web';

import styles from './DragHandle.scss';

type Props = Omit<
  ComponentProps<'button'>,
  'aria-label' | 'type' | 'children' | 'className'
> & {
  label: string;
  disabledReason: string | null;
};

// A blocked handle uses aria-disabled, not disabled, so keyboard users can still focus it
// and hear why it is blocked.
export function DragHandle({
  label,
  disabledReason,
  'aria-describedby': describedBy,
  ...buttonProps
}: Props) {
  const reasonId = useId();
  const descriptionIds = [describedBy, disabledReason && reasonId]
    .filter(Boolean)
    .join(' ');

  const button = (
    <button
      {...buttonProps}
      type="button"
      className={styles.handle}
      aria-label={label}
      aria-disabled={disabledReason ? true : undefined}
      aria-describedby={descriptionIds || undefined}
      data-test="drag-handle"
    >
      <Icon name="grip-vertical" />
    </button>
  );

  if (!disabledReason) {
    return button;
  }

  return (
    <Tooltip placement="top" TooltipComponent={disabledReason}>
      {button}
      <span id={reasonId} hidden>
        {disabledReason}
      </span>
    </Tooltip>
  );
}
